import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { useRecentSearchInline } from '@/components/feature/RecentSearchSuggest';
import ApiErrorAlert from '@/components/feedback/ApiErrorAlert';
import { useAuth } from '@/context/AuthContext';
import { clientErrorMessage } from '@/services/apiError';
import {
  searchCompanies,
  type EnterpriseSearchItem,
} from '@/services/enterpriseApi';
import { listAnalysisRecords, type AnalysisRecord } from '@/services/authApi';
import { findRecordByCompanyName } from '@/utils/companyNameMatch';
import {
  listRecentSearches,
  pushRecentSearch,
  type RecentSearchItem,
} from '@/utils/recentSearchHistory';

/**
 * 首页搜索词的本地记忆。
 *
 * 为什么要持久化：查询/分析失败后用户往往要回首页改关键词重试，而首页组件
 * 从单户页返回时会**重新挂载**，纯组件状态（useState）随之归零，输入框就空了，
 * 用户得重新敲一遍。存 sessionStorage 后，返回/刷新都能保留上次输入。
 */
const HERO_QUERY_KEY = 'tax-insight:hero-query';

function readHeroQuery(): string {
  try {
    return sessionStorage.getItem(HERO_QUERY_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveHeroQuery(value: string): void {
  try {
    sessionStorage.setItem(HERO_QUERY_KEY, value);
  } catch {
    /* 隐私模式 / 配额不足：忽略，退化为不记忆 */
  }
}

export default function HeroSection() {
  const navigate = useNavigate();
  const { user, requestLogin } = useAuth();
  /**
   * 登录态的最新副本（每渲染同步）。
   *
   * ⚠️ 登录拦截**不能读闭包里的 `user`**：pending 回调捕获的是游客那次渲染的
   * 闭包，登录成功后执行时 `user` 仍是 null，会再次走拦截分支——表现为
   * 「登录成功但原地不动、反复弹登录框」。读 ref 才能拿到登录后的新值。
   */
  const userRef = useRef(user);
  userRef.current = user;
  // 初始值取上次输入：失败返回 / 刷新都不丢关键词
  const [keyword, setKeyword] = useState<string>(() => readHeroQuery());
  const [candidates, setCandidates] = useState<EnterpriseSearchItem[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  /**
   * 失败原因弹窗。
   *
   * 输入框下方那行 `text-xs` 提示太容易被忽略（用户点完「分析」视线还在按钮上），
   * 所以失败时额外弹一次，把**具体原因**（额度不足 / 队列已满 / 网络中断…）显式摆到眼前。
   */
  const [failOpen, setFailOpen] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>(() =>
    listRecentSearches(),
  );
  /**
   * 历史分析记录缓存：把「输入的企业全称是否已分析过」的判定变成本地比较，
   * 命中就跳过模糊匹配接口。未登录/拉取失败时为 null，行为退回原链路。
   */
  const analysisRecordsRef = useRef<AnalysisRecord[] | null>(null);

  useEffect(() => {
    if (!user) {
      analysisRecordsRef.current = null;
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listAnalysisRecords();
        if (!cancelled) analysisRecordsRef.current = rows;
      } catch {
        // 拉不到就退回模糊匹配，不影响搜索本身
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  /**
   * 取历史分析记录（懒加载兜底）。
   *
   * 首屏那次请求可能还没回来，此时用户已点搜索——先等它，避免因为时序问题
   * 白跑一次模糊匹配。失败不缓存，下次搜索可重试。
   */
  const ensureAnalysisRecords = useCallback(async (): Promise<AnalysisRecord[]> => {
    if (!user) return [];
    if (analysisRecordsRef.current) return analysisRecordsRef.current;
    try {
      const rows = await listAnalysisRecords();
      analysisRecordsRef.current = rows;
      return rows;
    } catch {
      return [];
    }
  }, [user]);

  /** 已确认主体 → 进单户分析（模糊匹配选中 与 历史记录直达 共用） */
  const goAnalyze = useCallback(
    (target: {
      name: string;
      companyId?: string | null;
      creditCode?: string | null;
      region?: string | null;
      industry?: string | null;
    }) => {
      setConfirmOpen(false);
      pushRecentSearch({
        name: target.name,
        creditCode: target.creditCode ?? undefined,
      });
      setRecentSearches(listRecentSearches());
      const params = new URLSearchParams({
        q: target.name,
        companyId: target.companyId || '',
      });
      if (target.creditCode) params.set('creditCode', target.creditCode);
      if (target.region) params.set('region', target.region);
      if (target.industry) params.set('industry', target.industry);
      // 首页已确认主体：单户页直接开跑
      params.set('confirmed', '1');
      navigate(`/single-analysis?${params.toString()}`);
    },
    [navigate],
  );

  /** 首页搜企业 → 弹窗确认主体（对齐 Demo Hero），确认后进单户分析 */
  const handleAnalyze = async (overrideKeyword?: string) => {
    const k = (overrideKeyword ?? keyword).trim();
    if (!k) {
      setSearchError('请输入企业名称或信用代码');
      document.getElementById('hero-search')?.focus();
      return;
    }
    if (overrideKeyword) setKeyword(k);
    saveHeroQuery(k);
    setSearchError('');
    // 企业模糊检索会调用信数「企业名称速配」，是**按次计费**的付费动作：
    // 必须先登录（未登录不发起请求），登录成功后自动续接本次检索。
    // 额度校验在后端做（403 时弹窗给出原因），前端不重复判断以免两处口径漂移。
    if (!userRef.current) {
      requestLogin(() => {
        void handleAnalyze(k);
      });
      return;
    }
    setSearching(true);
    try {
      // 输入的就是历史分析记录里的企业全称 → 主体已确定，直接进分析，
      // 不必再调一次模糊匹配（既省请求，也省掉一次「只有一个候选」的确认弹窗）。
      const hit = findRecordByCompanyName(await ensureAnalysisRecords(), k);
      if (hit) {
        goAnalyze({
          name: hit.companyName,
          companyId: hit.companyId,
          creditCode: hit.creditCode,
          region: hit.region,
          industry: hit.industry,
        });
        return;
      }

      const data = await searchCompanies(k);
      if (data.items.length > 0) {
        setCandidates(data.items);
        setConfirmOpen(true);
        return;
      }
      // 无匹配：仍带词进单户页，由单户页展示失败态（未确认主体，不写入历史）
      navigate(`/single-analysis?${new URLSearchParams({ q: k }).toString()}`);
    } catch (err) {
      const reason = clientErrorMessage(err, '企业搜索失败，请稍后重试');
      setSearchError(reason);
      setFailReason(reason);
      setFailOpen(true);
    } finally {
      setSearching(false);
    }
  };

  const pickCompany = (c: EnterpriseSearchItem) => {
    goAnalyze({
      name: c.name,
      companyId: c.companyId,
      creditCode: c.creditCode,
      region: c.region,
      industry: c.industry,
    });
  };

  const pickRecent = (item: RecentSearchItem) => {
    setKeyword(item.name);
    saveHeroQuery(item.name);
    setSearchError('');
    document.getElementById('hero-search')?.focus();
  };

  const historyInline = useRecentSearchInline(keyword, (full) => {
    setKeyword(full);
    saveHeroQuery(full);
  });

  return (
    <section className="relative flex min-h-[600px] w-full flex-col justify-center overflow-hidden pb-16 pt-28 md:min-h-[660px] md:pb-20">
      <div className="absolute inset-0">
        <picture className="block h-full w-full">
          <source
            media="(max-width: 767px)"
            srcSet="https://readdy.ai/api/search-image?query=Vertical%20portrait%20background%20of%20a%20modern%20city%20skyline%20at%20dusk%2C%20tall%20glass%20office%20towers%20blurred%20with%20shallow%20depth%20of%20field%2C%20deep%20teal%20and%20dark%20emerald%20green%20tones%2C%20soft%20bokeh%20city%20lights%2C%20misty%20atmosphere%2C%20elegant%20corporate%20mood%2C%20dark%20enough%20for%20white%20text%20overlay%2C%20cinematic%20vertical%20composition%2C%20editorial%20photography%2C%20refined%20and%20professional%2C%20high%20detail&width=900&height=1200&seq=hero-skyline-green-portrait-2026&orientation=portrait"
          />
          <img
            src="https://readdy.ai/api/search-image?query=High-end%20business%20aesthetic%20background%20of%20a%20modern%20city%20skyline%20at%20dusk%20with%20blurred%20glass%20office%20towers%2C%20deep%20teal%20and%20dark%20emerald%20green%20tones%2C%20soft%20bokeh%20light%20spots%2C%20abstract%20architectural%20lines%20fading%20into%20darkness%2C%20cinematic%20wide%20composition%2C%20subtle%20mist%20and%20depth%20of%20field%2C%20professional%20corporate%20mood%2C%20dark%20enough%20for%20white%20text%20overlay%2C%20editorial%20photography%2C%20elegant%20and%20refined%2C%20high%20detail&width=1920&height=1080&seq=hero-skyline-green-2026&orientation=landscape"
            alt="税务洞察 TaxInsight 首页背景"
            title="税务洞察 TaxInsight 首页背景"
            className="hero-kenburns h-full w-full object-cover object-top"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/92 via-primary-900/85 to-primary-950/94" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary-700/45 via-transparent to-accent-900/35" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[820px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-600/30 blur-3xl" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent via-background-50/35 to-background-50 md:h-80" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4 md:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="ti-fade inline-flex items-center gap-2 rounded-full border border-primary-300/40 bg-background-50/10 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur-sm dark:text-foreground-950">
            <i className="ri-sparkling-2-line text-accent-300" />
            AI 时代 · 您的企业商机洞察助手
          </span>

          <h1 className="ti-rise mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-background-50 md:text-5xl dark:text-foreground-950">
            深挖客户价值
            <br />
            <span className="text-accent-300">精准发现每一笔潜在线索</span>
          </h1>

          <p className="ti-rise mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base dark:text-foreground-200">
            基于公开信息与经营事件，结合企业明细资料，为税务师事务所、财税咨询公司、律所与会计师事务所的涉税业务人员，
            提供从线索发现到合规交付的全链路支持。
          </p>

          <div className="ti-rise mx-auto mt-8 w-full max-w-2xl">
            <div
              className={`flex items-center gap-2 rounded-lg border bg-background-50 p-1.5 ${
                searchError ? 'border-accent-500' : 'border-background-300'
              }`}
            >
              <span className="ml-2 flex items-center text-foreground-400">
                <i className="ri-search-2-line text-lg" />
              </span>
              <div className="relative min-w-0 flex-1">
                {historyInline.ghostLayer}
                <input
                  id="hero-search"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    saveHeroQuery(e.target.value);
                    if (e.target.value.trim()) setSearchError('');
                  }}
                  onKeyDown={(e) => {
                    if (historyInline.onInputKeyDown(e)) return;
                    if (e.key === 'Enter') void handleAnalyze();
                  }}
                  aria-autocomplete="both"
                  aria-invalid={searchError ? true : undefined}
                  aria-describedby={searchError ? 'hero-search-error' : undefined}
                  title={historyInline.hasGhost ? '按 Tab 采纳历史补全' : undefined}
                  placeholder="输入企业名称 / 统一社会信用代码 / 品牌关键词"
                  className="relative z-10 h-11 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  autoComplete="off"
                />
              </div>
              <Button
                size="lg"
                onClick={() => void handleAnalyze()}
                disabled={searching}
                aria-busy={searching}
                className="px-7"
              >
                {searching ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-search-eye-line" />
                )}
                分析
              </Button>
            </div>

            {searchError ? (
              <ApiErrorAlert
                id="hero-search-error"
                message={searchError}
                tone="dark"
                className="mt-2 text-xs"
              />
            ) : null}
            {recentSearches.length > 0 ? (
              <div className="mt-3 text-left">
                <p className="text-xs text-primary-100/80">最近搜索</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {recentSearches.map((item) => (
                    <button
                      key={`${item.name}-${item.creditCode || ''}`}
                      type="button"
                      onClick={() => pickRecent(item)}
                      className="cursor-pointer rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 text-xs text-background-50 backdrop-blur-sm transition-colors hover:border-accent-300/60 hover:bg-background-50/20"
                    >
                      {item.label || item.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="mt-2 text-left">
              <Link
                to="/coming-soon/batch"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-100 hover:text-background-50 hover:underline dark:text-foreground-200 dark:hover:text-foreground-950"
              >
                <i className="ri-stack-line" />
                一次分析多家企业？使用批量分析
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="请确认企业主体"
        width="max-w-2xl"
      >
        <p className="mb-4 text-xs text-foreground-500">
          检索到 {candidates.length} 个匹配主体，请选择要分析的企业。企业名称可能重复，确认主体后再继续。
        </p>
        <div className="space-y-2">
          {candidates.map((c) => (
            <button
              key={c.companyId || c.creditCode || c.name}
              type="button"
              onClick={() => pickCompany(c)}
              className="flex min-h-11 w-full cursor-pointer items-center gap-4 rounded-md border border-background-200 bg-background-50 p-4 text-left transition-colors hover:border-primary-400 hover:bg-primary-50"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-building-4-line text-xl" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-foreground-900">{c.name}</span>
                <span className="mt-1 block text-xs text-foreground-500">
                  {[c.creditCode, c.region, c.industry].filter(Boolean).join(' · ') ||
                    '主体信息待补全'}
                </span>
              </span>
              <i className="ri-arrow-right-line text-foreground-400" />
            </button>
          ))}
        </div>
      </Modal>
      <Modal
        open={failOpen}
        onClose={() => setFailOpen(false)}
        title="分析未能发起"
        width="max-w-md"
      >
        <p
          role="alert"
          aria-live="assertive"
          className="rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm leading-relaxed text-accent-900"
        >
          <i className="ri-error-warning-line mr-1.5" aria-hidden="true" />
          {failReason}
        </p>
        <p className="mt-3 text-xs leading-relaxed text-foreground-500">
          本次未创建分析任务，也不扣减分析权益。可稍后重试，或更换企业主体。
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setFailOpen(false)}>
            知道了
          </Button>
          <Button
            onClick={() => {
              setFailOpen(false);
              void handleAnalyze(keyword);
            }}
          >
            重试
          </Button>
        </div>
      </Modal>
    </section>
  );
}
